// PumpFaceplate.jsx
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Zap } from 'lucide-react';
import TagChartPopup from './TagChartPopup';

const STYLE_ID = 'pump-fp-v3';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes fpIn      { from{opacity:0;transform:scale(.95) translateY(6px)} to{opacity:1;transform:none} }
    @keyframes fpRipple  { 0%{transform:scale(1);opacity:.8} 100%{transform:scale(3);opacity:0} }
    @keyframes fpBreathe { 0%,100%{opacity:.3} 50%{opacity:.75} }
    @keyframes fpScan    { 0%{top:-50px} 100%{top:200px} }
    @keyframes fpBlink   { 0%,100%{opacity:1} 50%{opacity:.15} }
    .fp3-x:hover   { background:rgba(239,68,68,.2)!important; color:#ff7070!important; }
    .fp3-trd:hover { opacity:.85; }
    .fp3-pill-on   { filter: drop-shadow(0 0 5px var(--pc)); }
  `;
  document.head.appendChild(s);
}

const SVG_NAMES = {
  pump3D:'3D Pump', centrifugalPump2:'Centrifugal Pump', centrifugalPump4:'Centrifugal Pump',
  classicPump1:'Classic Pump', coolPump:'Cool Pump', horizontalPump3:'Horizontal Pump',
  horizontalPump4:'Horizontal Pump', horizontalSplitCasePump:'Split Case Pump',
  magDriveNonMetallic:'Mag Drive Pump', magDrivePump2:'Mag Drive Pump',
  meteringPump4:'Metering Pump', seallessPump:'Sealless Pump',
  selfPrimingCentrifugalPump:'Self-Priming Pump', srhPump:'SRH Pump',
  verticalPump9:'Vertical Pump', yellowPump:'Pump Unit',
};

const BG  = '#0c111a';
const LBL = '#7b8fa8';

export default function PumpFaceplate({ comp, tagValues, PumpIconComponent, onClose, darkMode, plantId }) {
  const cfg = comp.pumpConfig || {};
  const [pos, setPos]             = useState(comp._initPos || { x: 140, y: 140 });
  const [trendOpen, setTrendOpen] = useState(false);
  const dragging = useRef(false);
  const dragRef  = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !dragRef.current) return;
      setPos({ x: Math.max(0, dragRef.current.px + e.clientX - dragRef.current.mx), y: Math.max(0, dragRef.current.py + e.clientY - dragRef.current.my) });
    };
    const up = () => { dragging.current = false; };
    const onFSChange = () => setPos(prev => ({
      x: Math.min(Math.max(prev.x, 10), window.innerWidth  - 340),
      y: Math.min(Math.max(prev.y, 10), window.innerHeight - 60),
    }));
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  }, []);

  const onDrag = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    dragging.current = true;
    dragRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  };

  /* ── derive status ── */
  const num = (t) => { const v = tagValues[t]; return v != null ? parseFloat(v) : null; };
  const onV  = cfg.onFeedback?.tagName  ? num(cfg.onFeedback.tagName)  : null;
  const offV = cfg.offFeedback?.tagName ? num(cfg.offFeedback.tagName) : null;
  const tripV= cfg.trip?.tagName        ? num(cfg.trip.tagName)        : null;
  const rlV  = cfg.remoteLocal?.tagName ? num(cfg.remoteLocal.tagName) : null;
  const modeV= cfg.mode?.tagName        ? num(cfg.mode.tagName)        : null;
  const onCV = cfg.onCommand?.tagName   ? num(cfg.onCommand.tagName)   : null;
  const offCV= cfg.offCommand?.tagName  ? num(cfg.offCommand.tagName)  : null;

  let SC = cfg.offFeedback?.color || comp.defaultColor || '#64748b';
  let SL = cfg.offFeedback?.label || 'Stopped';
  if (cfg.trip?.tagName && tripV !== null && tripV >= 1) {
    SC = cfg.trip.color; SL = cfg.trip.label || 'Trip';
  } else if (cfg.onFeedback?.tagName && cfg.offFeedback?.tagName && onV !== null && offV !== null) {
    if      (onV >= 1 && offV >= 1)  { SC = cfg.faultColor || '#f59e0b';       SL = 'Fault'; }
    else if (onV === 0 && offV === 0){ SC = cfg.intermediateColor || '#fb923c'; SL = 'Intermediate'; }
    else if (onV >= 1)               { SC = cfg.onFeedback.color;              SL = cfg.onFeedback.label || 'Running'; }
  } else if (cfg.onFeedback?.tagName && onV !== null && onV >= 1) {
    SC = cfg.onFeedback.color; SL = cfg.onFeedback.label || 'Running';
  }

  const isRunning = SL === (cfg.onFeedback?.label || 'Running');
  const isFault   = ['fault','trip'].includes(SL.toLowerCase());

  const rlColor = rlV == null ? '#64748b' : (rlV >= 1 ? (cfg.remoteLocal?.localColor  || '#22c55e') : (cfg.remoteLocal?.remoteColor || '#6366f1'));
  const rlLabel = rlV == null ? '—'       : (rlV >= 1 ? 'LOCAL' : 'REMOTE');
  const modeLbl = modeV == null ? '—'     : (modeV >= 1 ? 'AUTO' : 'MANUAL');
  const modeClr = modeV == null ? '#64748b' : (modeV >= 1 ? '#10b981' : '#f59e0b');
  let cmdLbl = 'IDLE', cmdClr = '#64748b';
  if (onCV !== null && onCV >= 1)        { cmdLbl = 'START'; cmdClr = cfg.onCommand?.color  || '#3b82f6'; }
  else if (offCV !== null && offCV >= 1) { cmdLbl = 'STOP';  cmdClr = cfg.offCommand?.color || '#e2e8f0'; }

  const allTags = [
    cfg.onFeedback?.tagName, cfg.offFeedback?.tagName, cfg.trip?.tagName,
    cfg.onCommand?.tagName, cfg.offCommand?.tagName, cfg.mode?.tagName, cfg.remoteLocal?.tagName,
  ].filter(Boolean);

  const rows = [
    { lbl: 'Feedback',       val: SL.toUpperCase(), clr: SC      },
    ...(onCV != null || offCV != null ? [{ lbl: 'Command',       val: cmdLbl,  clr: cmdClr  }] : []),
    ...(cfg.remoteLocal?.tagName      ? [{ lbl: 'Remote / Local', val: rlLabel, clr: rlColor }] : []),
    ...(cfg.mode?.tagName             ? [{ lbl: 'Mode',           val: modeLbl, clr: modeClr }] : []),
  ];

  const pills = [
    { c: cfg.onFeedback,  lbl: 'ON FB'  },
    { c: cfg.offFeedback, lbl: 'OFF FB' },
    { c: cfg.trip,        lbl: 'TRIP'   },
  ].filter(x => x.c?.tagName);

  const trendPos = { x: Math.min(pos.x + 355, window.innerWidth - 585), y: Math.max(pos.y, 10) };

  const plate = (
    <div
      ref={panelRef}
      style={{
        position: 'fixed', left: pos.x, top: pos.y, zIndex: 9999,
        width: '340px',
        background: BG,
        borderRadius: '14px',
        border: `1px solid ${SC}30`,
        boxShadow: `0 0 0 1px rgba(0,0,0,.5), 0 0 36px ${SC}14, 0 20px 60px rgba(0,0,0,.75)`,
        overflow: 'hidden',
        userSelect: 'none',
        animation: 'fpIn .2s cubic-bezier(.16,1,.3,1)',
        transition: 'border-color .5s, box-shadow .5s',
        fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
      }}
    >
      {/* neon top line */}
      <div style={{ height: '1.5px', background: `linear-gradient(90deg,transparent,${SC}bb 40%,${SC} 50%,${SC}bb 60%,transparent)`, transition: 'background .5s' }} />

      {/* ── HEADER ── */}
      <div onMouseDown={onDrag} style={{ padding: '12px 12px 0', display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'grab' }}>
        {/* status dot */}
        <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: SC, flexShrink: 0, marginTop: '5px', transition: 'background .4s' }} />

        {/* label + type */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: '700', color: '#dde6f0', letterSpacing: '.2px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {comp.label || 'Pump'}
          </div>
          <div style={{ fontSize: '9px', color: LBL, letterSpacing: '.5px', textTransform: 'uppercase', marginTop: '1px' }}>
            {SVG_NAMES[comp.type] || 'Pump Unit'}
          </div>
        </div>

        {/* Trend button — only if tags exist */}
        {allTags.length > 0 && (
          <button onClick={() => setTrendOpen(v => !v)} title="Trend" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '28px', height: '28px', borderRadius: '8px',
            border: `1px solid ${trendOpen ? SC : 'rgba(255,255,255,.20)'}`,
            background: trendOpen ? SC + '25' : 'rgba(255,255,255,.08)',
            cursor: 'pointer', transition: 'all .2s', flexShrink: 0,
            padding: 0,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke={trendOpen ? SC : '#ffffff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* close */}
        <button className="fp3-x" onClick={onClose} style={{
          width: '28px', height: '28px', borderRadius: '8px',
          border: '1px solid rgba(255,255,255,.20)',
          background: 'rgba(255,255,255,.08)',
          color: '#aab4c0', cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '17px', lineHeight: 1, fontWeight: '300', transition: 'all .15s',
        }}>×</button>
      </div>

      {/* ── BODY: 2 columns ── */}
      <div onMouseDown={onDrag} style={{ display: 'flex', padding: '14px 12px 10px', gap: '0', cursor: 'grab', alignItems: 'center' }}>

        {/* LEFT — image + badge + run hours */}
        <div style={{ width: '104px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative', lineHeight: 0 }}>
            <div style={{
              position: 'absolute', inset: '-10px', borderRadius: '50%',
              background: `radial-gradient(circle, ${SC}28 0%, transparent 68%)`,
              transition: 'background .5s',
              animation: isRunning ? 'fpBreathe 2.4s ease-in-out infinite' : 'none',
              pointerEvents: 'none',
            }} />
            {isRunning && (
              <div style={{
                position: 'absolute', left: 0, right: 0, height: '32px',
                background: `linear-gradient(180deg,transparent,${SC}18,transparent)`,
                animation: 'fpScan 2.2s linear infinite', pointerEvents: 'none',
              }} />
            )}
            <div style={{ position: 'relative', zIndex: 1 }}>
              {PumpIconComponent ? <PumpIconComponent width={96} height={96} /> : <Zap size={52} color={SC} />}
            </div>
          </div>

          {/* run hours */}
          <div style={{ textAlign: 'center', lineHeight: 1.4 }}>
            <div style={{ fontSize: '9px', color: LBL, letterSpacing: '.3px' }}>Run Hours:</div>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#8898aa', fontVariantNumeric: 'tabular-nums', letterSpacing: '.4px' }}>
              00:00:00
            </div>
          </div>
        </div>

        {/* RIGHT — status rows */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: '16px' }}>
          {rows.map(({ lbl, val, clr }, i) => (
            <div key={lbl} style={{
              paddingTop:    i === 0 ? '0' : '9px',
              paddingBottom: '9px',
              borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,.04)' : 'none',
            }}>
              <div style={{ fontSize: '9px', color: LBL, fontWeight: '500', letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: '3px' }}>
                {lbl}
              </div>
              <div style={{
                fontSize: '17px', fontWeight: '800', color: clr, letterSpacing: '.2px', lineHeight: 1,
                textShadow: `0 0 18px ${clr}44`, transition: 'color .4s, text-shadow .4s',
                animation: isFault && lbl === 'Feedback' ? 'fpBlink .8s ease-in-out infinite' : 'none',
              }}>
                {val}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TAG PILLS ── */}
      {pills.length > 0 && (
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap', padding: '0 12px 12px' }}>
          {pills.map(({ c, lbl }) => {
            const raw = tagValues[c.tagName];
            const on  = raw !== undefined && parseFloat(raw) >= 1;
            return (
              <div key={lbl} className={on ? 'fp3-pill-on' : ''} style={{ '--pc': c.color }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '5px',
                  padding: '3px 9px', borderRadius: '20px',
                  background: on ? c.color + '18' : 'rgba(255,255,255,.03)',
                  border: `1px solid ${on ? c.color + '55' : 'rgba(255,255,255,.07)'}`,
                  transition: 'all .25s',
                }}>
                  <div style={{
                    width: '5px', height: '5px', borderRadius: '50%',
                    background: on ? c.color : '#1e2d3e',
                    boxShadow: on ? `0 0 8px ${c.color}` : 'none',
                    transition: 'all .25s',
                    animation: on ? 'fpBreathe 1.6s ease-in-out infinite' : 'none',
                  }} />
                  <span style={{ fontSize: '9px', fontWeight: '700', color: on ? c.color : LBL, letterSpacing: '.8px' }}>{lbl}</span>
                  <span style={{ fontSize: '9px', color: on ? c.color + 'bb' : '#1e2d3e', fontWeight: '500' }}>{raw !== undefined ? raw : '—'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* bottom accent line */}
      <div style={{ height: '1px', background: `linear-gradient(90deg,transparent,${SC}55 50%,transparent)`, transition: 'background .5s' }} />
    </div>
  );

  return (
    <>
      {createPortal(plate, document.body)}
      {trendOpen && allTags.length > 0 && (
        <TagChartPopup
          tags={allTags}
          position={trendPos}
          componentLabel={comp.label || 'Pump Trend'}
          onClose={() => setTrendOpen(false)}
          darkMode={darkMode}
          plantId={plantId}
        />
      )}
    </>
  );
}
